import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  LayoutDashboard, Receipt, FolderKanban, Users, BarChart3, Calendar,
  FileText, Bot, Settings, HelpCircle, Upload, Keyboard, Plug,
  Wallet, TrendingUp, TrendingDown, Calculator, Layers, Zap, 
  CheckCircle2, AlertTriangle
} from 'lucide-react';

const faqSections = [
  {
    id: 'setup-checklist',
    icon: CheckCircle2,
    title: 'Первоначальная настройка',
    description: 'Чек-лист для начала работы',
    questions: [
      {
        q: 'Что нужно настроить после первого входа?',
        a: `Для полноценной работы MK Finance выполните следующие шаги:

**1. Основные справочники (Настройки):**
- ✅ Счета — добавьте все банковские счета и кассы
- ✅ Категории — настройте статьи доходов и расходов
- ✅ Направления — обычно уже созданы (Теплицы, Сауны, Купели)
- ✅ Контрагенты — добавьте основных клиентов и поставщиков

**2. Интеграции (Настройки → Интеграции):**
- ⚙️ Telegram Bot — для получения финансовых сводок

**3. Автоматизация (Планирование → Автоправила):**
- ⚙️ Правила категоризации — для автоматического присвоения категорий при импорте

**4. Документы:**
- 📎 Загрузите первые документы (счета, акты, выписки)`
      },
      {
        q: 'Как настроить Telegram бота?',
        a: `**Шаг 1: Создайте бота**
1. Откройте Telegram и найдите @BotFather
2. Отправьте команду /newbot
3. Придумайте имя бота (например: MK Finance Bot)
4. Придумайте username (например: alfinance_bot)
5. Скопируйте **Bot Token** (длинная строка вида 123456:ABC...)

**Шаг 2: Получите Chat ID**
1. Найдите бота @userinfobot или @getidsbot
2. Отправьте /start
3. Скопируйте ваш **Chat ID** (число, например: 123456789)
4. Или ID группы с минусом (-1001234567890)

**Шаг 3: Настройте в MK Finance**
1. Перейдите в Настройки → Интеграции
2. Вставьте Bot Token и Chat ID
3. Нажмите "Тест подключения"
4. Включите "Автоматические сводки"
5. Выберите расписание и время
6. Сохраните настройки

**Готово!** Теперь бот будет присылать:
- 📊 Финансовые сводки по расписанию
- 📅 **Напоминания о плановых платежах** каждый день в 09:30:
  - 🔴 Просроченные
  - 🟡 К оплате сегодня
  - 🟢 К оплате завтра

Также можно нажать кнопку **«Напомнить о платежах»** на странице Интеграций, чтобы получить напоминание прямо сейчас (для теста).`
      },
      {
        q: 'Какие данные нужны для правил автоматизации?',
        a: `Правила автоматизации работают при импорте банковских выписок.

**Что настроить:**
1. Перейдите в Планирование → Автоправила
2. Создайте правило для каждого частого контрагента:

**Примеры правил:**
| Шаблон | Категория | Направление |
|--------|-----------|-------------|
| BIEDRONKA | Офисные расходы | Общее |
| ALLEGRO | Закупка материалов | - |
| OLX | Реклама | - |
| ORLEN | Транспорт | Общее |
| ZUS | Налоги и сборы | Общее |
| US SKARBOWY | Налоги и сборы | Общее |

**Совет:** Добавляйте правила постепенно. После каждого импорта смотрите, какие операции остались без категории, и создавайте для них правила.`
      }
    ]
  },
  {
    id: 'getting-started',
    icon: HelpCircle,
    title: 'Начало работы',
    description: 'Основы работы с MK Finance',
    questions: [
      {
        q: 'Как войти в систему?',
        a: `На странице входа введите ваш email и пароль, или используйте логин администратора:
        
**Для администратора:**
- Логин: admin
- Пароль: ваш установленный пароль

**Для обычных пользователей:**
- Введите email, указанный при регистрации
- Введите пароль

После входа вы попадёте на главную страницу — Рабочий стол (Dashboard).`
      },
      {
        q: 'Какие роли пользователей есть в системе?',
        a: `В MK Finance есть 4 роли:

1. **Супер-администратор** — полный доступ ко всем функциям, включая настройки системы
2. **Владелец (Owner)** — полный доступ к финансовым данным своей компании
3. **Бухгалтер (Accountant)** — доступ к операциям, отчётам и документам
4. **Менеджер (Manager)** — ограниченный доступ: просмотр дашборда и своих операций`
      },
      {
        q: 'Как создать первую операцию?',
        a: `1. Перейдите в раздел **Операции** в боковом меню
2. Нажмите кнопку **+ Доход** или **+ Расход**
3. Заполните форму:
   - Дата операции
   - Сумма и валюта (PLN, EUR, USD)
   - Категория (статья)
   - Направление бизнеса (Теплицы, Сауны, Купели, Общее)
   - Счёт
   - Контрагент (опционально)
   - Описание (опционально)
4. Нажмите **Сохранить**

💡 **Совет:** Используйте горячую клавишу **N** для быстрого создания операции.`
      }
    ]
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Рабочий стол (Dashboard)',
    description: 'Главная страница с ключевыми показателями',
    questions: [
      {
        q: 'Что показывает рабочий стол?',
        a: `Рабочий стол — это главная страница с обзором финансов вашего бизнеса:

**Карточки показателей:**
- 💰 **Доходы** — сумма всех поступлений за период
- 💸 **Расходы** — сумма всех трат за период
- 📊 **Прибыль** — разница между доходами и расходами
- 🏦 **Деньги бизнеса** — общий баланс на всех счетах

**Графики:**
- **Деньги на счетах** — динамика баланса по дням
- **Прибыль по направлениям** — распределение прибыли между Теплицами, Саунами, Купелями

**Виджеты:**
- **Счета** — баланс каждого счёта
- **Ближайшие платежи** — запланированные операции
- **Топ контрагентов** — самые активные партнёры`
      },
      {
        q: 'Как изменить период на дашборде?',
        a: `В правом верхнем углу есть селектор периода. Доступные опции:

- **Текущий месяц** — с 1 числа текущего месяца по сегодня
- **Прошлый месяц** — весь предыдущий месяц
- **Квартал** — последние 3 месяца
- **Год** — с начала года

Все карточки и графики автоматически пересчитаются для выбранного периода.`
      }
    ]
  },
  {
    id: 'transactions',
    icon: Receipt,
    title: 'Операции',
    description: 'Управление финансовыми операциями',
    questions: [
      {
        q: 'Какие типы операций поддерживаются?',
        a: `В системе есть 3 типа операций:

1. **Доход** (зелёный) — поступления денег: продажи, предоплаты, возвраты
2. **Расход** (красный) — траты: закупки, зарплаты, аренда
3. **Перевод** (синий) — перемещение между своими счетами

Каждая операция имеет статус:
- **Факт** — реально совершённая операция
- **План** — запланированная операция`
      },
      {
        q: 'Как фильтровать операции?',
        a: `На странице операций доступны фильтры:

- **Период** — текущий/прошлый месяц, квартал, год
- **Тип** — доход, расход, перевод или все
- **Статус** — факт или план
- **Счёт** — конкретный счёт
- **Направление** — Теплицы, Сауны, Купели
- **Поиск** — по описанию или контрагенту

💡 Используйте горячую клавишу **F** или **/** для быстрого поиска.`
      },
      {
        q: 'Как прикрепить документ к операции?',
        a: `1. Откройте список операций
2. Найдите нужную операцию и нажмите на меню (три точки справа)
3. Выберите **Документы**
4. В появившемся окне:
   - Если у вас есть загруженные документы без привязки, они появятся в списке
   - Нажмите **Прикрепить** напротив нужного документа
   - Документ привяжется к операции

Чтобы загрузить новый документ, сначала перейдите в раздел **Документы** и загрузите файл.`
      }
    ]
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Аналитика',
    description: 'Отчёты и анализ финансов',
    questions: [
      {
        q: 'Какие отчёты доступны?',
        a: `В разделе Аналитика доступно 5 отчётов:

1. **Движение денег (Cash Flow)** — приток и отток денег по месяцам
2. **Прибыли и убытки (P&L)** — доходы минус расходы с детализацией по статьям
3. **Баланс** — активы (деньги на счетах), обязательства (плановые расходы), дебиторка (плановые доходы)
4. **Анализ расходов** — детальная разбивка расходов по категориям, направлениям и контрагентам
5. **Рентабельность** — прибыльность каждого направления бизнеса с расчётом маржи`
      },
      {
        q: 'Как работает отчёт "Баланс"?',
        a: `Отчёт Баланс показывает финансовое состояние на определённую дату:

**Активы:**
- Наличные (касса)
- Расчётные счета (банк)
- Карты
- Сбережения

**Обязательства:**
- Запланированные расходы (что нужно заплатить)

**Дебиторка:**
- Запланированные доходы (что должны вам)

**Чистый капитал** = Активы - Обязательства + Дебиторка`
      },
      {
        q: 'Как анализировать рентабельность?',
        a: `Отчёт "Рентабельность" показывает эффективность каждого направления:

**Показатели:**
- **Выручка** — все доходы направления
- **Расходы** — все затраты направления
- **Прибыль** — выручка минус расходы
- **Маржа** — прибыль / выручка × 100%

**Оценка маржи:**
- 🟢 **Высокая** — от 30%
- 🟡 **Средняя** — 15-30%
- 🟠 **Низкая** — 0-15%
- 🔴 **Убыток** — меньше 0%

Используйте этот отчёт, чтобы понять какие направления наиболее прибыльны.`
      }
    ]
  },
  {
    id: 'documents',
    icon: FileText,
    title: 'Документы',
    description: 'Управление документами и файлами',
    questions: [
      {
        q: 'Какие документы можно загружать?',
        a: `Поддерживаются все основные форматы:
- **PDF** — счета, акты, договоры
- **Изображения** — JPG, PNG (фото чеков, накладных)
- **Excel/CSV** — банковские выписки
- **Word** — договоры

**Типы документов:**
- Счёт (invoice)
- Банковская выписка (bank_statement)
- Платёжное поручение (payment_order)
- Акт (act)
- Договор (contract)
- Чек (receipt)
- Прочее (other)`
      },
      {
        q: 'Как загрузить документ?',
        a: `1. Перейдите в раздел **Документы**
2. Перетащите файл в область загрузки (drag & drop)
3. Или нажмите на область и выберите файл
4. Документ загрузится и появится в списке

После загрузки вы можете:
- Указать тип документа
- Привязать к операции
- Указать период (месяц)
- Добавить описание`
      },
      {
        q: 'Как экспортировать документы?',
        a: `1. Выберите период (месяц) в фильтре
2. Нажмите **Экспорт** в правом верхнем углу
3. Система создаст ZIP-архив со всеми документами за период
4. Архив автоматически скачается

Это удобно для передачи бухгалтеру или архивации.`
      }
    ]
  },
  {
    id: 'planning',
    icon: Calendar,
    title: 'Планирование',
    description: 'Платёжный календарь, регулярные расходы и зарплаты',
    questions: [
      {
        q: 'Как работает платёжный календарь?',
        a: `Платёжный календарь показывает запланированные операции:

**Виды плановых операций:**
- **Разовые** — конкретная дата (например, оплата счёта)
- **Регулярные** — повторяющиеся (аренда, зарплата, абонплаты) — создаются автоматически из шаблонов «Регулярные расходы»

**Статусы:**
- 🟢 **Оплачен** — связан с фактической операцией
- 🟡 **Ожидает** — срок ещё не наступил
- 🔴 **Просрочено** — дата прошла, а платёж не связан с операцией

При наступлении даты плановая операция автоматически становится просроченной.`
      },
      {
        q: 'Как связать плановый платёж с фактической операцией?',
        a: `Это ключевой механизм сверки — реальные деньги вносятся через банковские выписки или вручную в «Операциях», а календарь только сверяется с фактом, **не дублируя** записи.

**Шаги:**
1. На странице **Платёжный календарь** найдите плановый платёж
2. Нажмите кнопку с иконкой 🔗 (Link2) рядом со статусом
3. Откроется диалог «Связать с операцией» с предложениями:
   - Окно поиска: ±10 дней от даты планового платежа
   - Сумма: ±15% от плановой
   - Совпадение по категории — приоритет
4. Кликните по подходящей операции
5. Плановый платёж переходит в статус **Оплачен** + сохраняется ссылка на транзакцию

**Если кандидатов нет:**
Введите фактическую операцию вручную в разделе «Операции» (или дождитесь импорта банковской выписки), затем повторите шаг.

**Отвязать:**
Кликните по галочке (✅) и иконке Unlink — статус вернётся в Pending.`
      },
      {
        q: 'Как создать плановый платёж?',
        a: `1. Перейдите в **Планирование → Платёжный календарь**
2. Нажмите **+ Добавить**
3. Заполните:
   - Тип (доход/расход)
   - Дата платежа
   - Сумма и валюта
   - Категория
   - Контрагент (от кого/кому)
   - Включите повтор, если операция регулярная
4. Нажмите **Создать**

Платёж появится в календаре и на дашборде в виджете "Ближайшие платежи".

💡 **Совет:** для постоянных платежей используйте «Регулярные расходы» — система сама будет создавать плановые платежи раз в месяц.`
      },
      {
        q: 'Что такое «Регулярные расходы»?',
        a: `Шаблоны постоянных платежей: аренда, интернет, CRM, бухгалтер, налоги, реклама и т.д.

**Как использовать:**
1. **Планирование → Платёжный календарь → вкладка «Шаблоны» → + Добавить**
2. Заполните:
   - Название («Аренда склада»)
   - Сумма + валюта
   - Периодичность: ежемесячно / ежеквартально
   - День месяца (1-28)
   - Статья (категория), направление, счёт
3. Сохраните

**Что происходит дальше:**
- Каждый день в **03:00** система проверяет активные шаблоны и создаёт плановый платёж в календаре на ближайший срок (без дублей)
- Можно нажать кнопку **«Создать платежи сейчас»** для немедленной генерации
- В сводке вверху видны: количество шаблонов, сумма / месяц, сумма / год

**Сверка с фактом:**
Когда придёт время — оплатите как обычно (банк/касса). На странице «Платёжный календарь» нажмите 🔗 «Связать» рядом с этим платежом — система предложит подходящую операцию.`
      },
      {
        q: 'Как работает модуль «Зарплаты»?',
        a: `Расчётная часть ФОТ — отдельно от движения денег. Учёт зарплат в три шага:

**1. Сотрудники** (Планирование → Зарплаты → таб «Сотрудники»):
- Имя, должность, оклад по умолчанию, валюта, направление, статус (активен/архив)

**2. Начисления** (таб «Начисления»):
- Раз в месяц — кнопка **«Создать по окладам»** массово создаст начисления на текущий месяц по окладам по умолчанию
- Или вручную **«+ Начислить»** — выбираете сотрудника, корректируете оклад, добавляете премию / удержания
- Итого «К выплате» = Оклад + Премия − Удержания (считается автоматически)

**3. Сверка с операциями:**
- На каждом начислении кнопка **«Связать»** — открывает диалог с подходящими расходными операциями (окно: текущий месяц + ±10 дней)
- Кликом отмечаете «Выплачено», сохраняется ссылка на конкретную транзакцию

**Виджет ФОТ на дашборде:**
- Начислено / Выплачено / Осталось (за текущий месяц)
- Разбивка по направлениям
- % от доходов

⚠️ Зарплаты **не создают** транзакций. Реальные выплаты — через выписку банка или ручной ввод в «Операциях».`
      }
    ]
  },
  {
    id: 'runway-fixed',
    icon: TrendingDown,
    title: 'Runway и постоянные расходы',
    description: 'На сколько месяцев хватит денег',
    questions: [
      {
        q: 'Что такое виджет «Runway» на дашборде?',
        a: `**Runway** = сколько месяцев бизнес проживёт на текущем остатке при текущих постоянных расходах.

**Формула:**
\`Runway = Остаток денег / Средний burn по постоянным расходам за 3 месяца\`

**Цветовая кодировка:**
- 🟢 ≥ 6 месяцев — комфортно
- 🟡 3-6 месяцев — стоит подумать о росте выручки
- 🔴 < 3 месяцев — критично, нужен план

**Что считается:**
- В числителе: суммарный баланс всех счетов в PLN (EUR пересчитывается по курсу NBP)
- В знаменателе: средние расходы за последние 3 полных месяца **по статьям, помеченным "Постоянный расход"**

Ниже виджета — топ-6 постоянных статей, чтобы увидеть, что съедает больше всего.`
      },
      {
        q: 'Как отметить категорию как «Постоянный расход»?',
        a: `1. Перейдите в **Настройки → Статьи**
2. Кликните на расходную статью (карандаш)
3. Поставьте галочку **«Постоянный расход»**
4. Сохраните

**Что отмечать:**
- ✅ Аренда, коммуналка
- ✅ Зарплаты, налоги, страховки
- ✅ Интернет, CRM, хостинг, бухгалтер
- ✅ Реклама / маркетинг (если стабильно каждый месяц)

**Что НЕ отмечать:**
- ❌ Закупка материалов (себестоимость — переменные расходы)
- ❌ Логистика (зависит от объёма продаж)
- ❌ Разовые покупки

После отметки сразу появляется бейдж «Постоянный» рядом со статьёй и виджет Runway на дашборде начинает считать.`
      }
    ]
  },
  {
    id: 'automation',
    icon: Zap,
    title: 'Автоматизация',
    description: 'Правила автокатегоризации',
    questions: [
      {
        q: 'Что такое правила автоматизации?',
        a: `Правила автоматизации упрощают импорт операций из банковских выписок.

**Как работают:**
При импорте CSV-файла система проверяет описание каждой операции. Если описание содержит ваш шаблон, операция автоматически получает:
- Категорию
- Направление  
- Контрагента

**Пример:**
Если в описании есть "BIEDRONKA", система автоматически поставит:
- Категория: "Офисные расходы"
- Направление: "Общее"
- Контрагент: "Biedronka"`
      },
      {
        q: 'Как создать правило?',
        a: `1. Перейдите в **Планирование → Автоправила**
2. Нажмите **+ Новое правило**
3. Укажите:
   - **Шаблон** — текст для поиска в описании (например: ALLEGRO, BIEDRONKA, OLX)
   - **Категория** — какую статью присвоить
   - **Направление** — какое направление присвоить
   - **Контрагент** — какого контрагента присвоить
4. Нажмите **Создать**

⚠️ Поиск шаблона не учитывает регистр: "allegro" найдёт и "ALLEGRO", и "Allegro".`
      }
    ]
  },
  {
    id: 'import',
    icon: Upload,
    title: 'Импорт данных',
    description: 'Загрузка банковских выписок',
    questions: [
      {
        q: 'Как импортировать банковскую выписку?',
        a: `1. Перейдите в **Планирование → Импорт выписок**
2. Загрузите CSV-файл (выгрузите из интернет-банка)
3. Укажите настройки маппинга:
   - Какая колонка содержит дату
   - Какая колонка содержит сумму
   - Какая колонка содержит описание
4. Выберите счёт для импорта
5. Нажмите **Импортировать**

Система проверит операции правилами автоматизации и присвоит категории.`
      }
    ]
  },
  {
    id: 'ai',
    icon: Bot,
    title: 'AI Ассистент',
    description: 'Чат с финансовым помощником',
    questions: [
      {
        q: 'Что умеет AI ассистент?',
        a: `AI ассистент (кнопка в правом нижнем углу) помогает с финансовыми вопросами:

**Может ответить на:**
- "Какая прибыль за этот месяц?"
- "Сколько денег на счетах?"
- "Какие самые большие расходы?"
- "Сравни доходы за последние 3 месяца"

**Может помочь с:**
- Быстрым анализом финансов
- Поиском проблемных статей
- Рекомендациями по оптимизации

AI использует данные вашей компании и даёт персонализированные ответы.`
      },
      {
        q: 'Как получать сводки в Telegram?',
        a: `MK Finance поддерживает интеграцию с Telegram-ботом для получения сводок:

**Доступные команды:**
- /summary day — сводка за сегодня
- /summary week — сводка за неделю
- /summary month — сводка за месяц

**Сводка включает:**
- Общие показатели (доходы, расходы, прибыль)
- Баланс на счетах
- Разбивку по направлениям
- Топ расходов
- Ожидаемые платежи

Для настройки обратитесь к администратору.`
      }
    ]
  },
  {
    id: 'hotkeys',
    icon: Keyboard,
    title: 'Горячие клавиши',
    description: 'Быстрая навигация',
    questions: [
      {
        q: 'Какие горячие клавиши доступны?',
        a: `**Навигация:**
- **D** — перейти на Рабочий стол (Dashboard)
- **N** — перейти в Операции / создать новую

**Поиск:**
- **F** или **/** — фокус на поле поиска

**Общие:**
- **Escape** — закрыть диалоговое окно

💡 Горячие клавиши работают, когда вы не находитесь в поле ввода текста.`
      }
    ]
  },
  {
    id: 'settings',
    icon: Settings,
    title: 'Настройки',
    description: 'Управление справочниками',
    questions: [
      {
        q: 'Как добавить новый счёт?',
        a: `1. Перейдите в **Настройки → Счета**
2. Нажмите **+ Новый счёт**
3. Укажите:
   - Название (например: "ПриватБанк UAH")
   - Тип (расчётный, наличные, карта, сбережения)
   - Валюта (PLN, EUR, USD)
   - Банк (опционально)
   - Начальный остаток
4. Нажмите **Сохранить**

Счёт появится в списке и станет доступен при создании операций.`
      },
      {
        q: 'Как управлять категориями?',
        a: `1. Перейдите в **Настройки → Категории**
2. Категории разделены на **Доходы** и **Расходы**
3. Каждая категория имеет **группу** (например: Выручка, Себестоимость)
4. Для добавления нажмите **+ Категория**
5. Для редактирования нажмите на иконку карандаша

**Стандартные группы расходов:**
- Себестоимость (закупка материалов, производство, логистика)
- Операционные расходы (аренда, зарплата, реклама)
- Налоги и сборы
- Прочие расходы`
      },
      {
        q: 'Как добавить контрагента?',
        a: `1. Перейдите в **Контрагенты** в меню
2. Нажмите **+ Новый контрагент**
3. Укажите:
   - Название компании или ФИО
   - Тип (Клиент, Поставщик, Партнёр)
   - Группа (для организации)
   - Email и телефон (опционально)
4. Нажмите **Сохранить**

Контрагенты появятся в выпадающих списках при создании операций.`
      },
      {
        q: 'Как сделать резервную копию базы данных?',
        a: `**Настройки → вкладка «Бэкап»**

**Скачать архив:**
1. Кнопка **«Статистика»** — увидеть, сколько документов в каждой коллекции (проверка что это та БД)
2. Кнопка **«Скачать архив»** — браузер скачает файл \`wmfinance-db-YYYYMMDD-HHMMSS.tar.gz\` со всеми коллекциями MongoDB

**Что внутри архива:**
- Все пользователи, транзакции, счета, категории, направления
- Документы, проекты, контрагенты
- Шаблоны регулярных расходов, сотрудники, начисления
- Настройки интеграций (включая токены)

⚠️ Храните архив в безопасном месте — он даёт полный доступ к вашему аккаунту.

**Восстановить из архива:**
1. Та же вкладка «Бэкап» → блок «Импорт базы данных»
2. Выберите файл \`.tar.gz\`
3. Нажмите **«Восстановить»** → подтвердите (текущая БД будет полностью перезаписана)

**Альтернатива через терминал** (для VPS):
\`\`\`bash
tar xzf wmfinance-db-YYYYMMDD.tar.gz
export MONGO_URL="mongodb://localhost:27017"
export DB_NAME="alfinance"
python3 deploy/import-db.py
\`\`\``
      }
    ]
  }
];

export const FAQPage = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Справка и FAQ</h1>
        <p className="text-muted-foreground mt-1">Подробное руководство по работе с MK Finance</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Navigation */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Разделы</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {faqSections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    title={section.title}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors ${
                      activeSection === section.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted'
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{section.title}</span>
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-6">
          {faqSections.map((section) => {
            const Icon = section.icon;
            return (
              <Card 
                key={section.id} 
                id={section.id}
                className={activeSection === section.id ? 'ring-2 ring-primary' : ''}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    {section.title}
                  </CardTitle>
                  <CardDescription>{section.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {section.questions.map((item, idx) => (
                      <AccordionItem key={idx} value={`${section.id}-${idx}`}>
                        <AccordionTrigger className="text-left">
                          {item.q}
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="prose prose-sm dark:prose-invert max-w-none">
                            {item.a.split('\n').map((line, i) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return <h4 key={i} className="font-semibold mt-4 mb-2">{line.replace(/\*\*/g, '')}</h4>;
                              }
                              if (line.startsWith('- ')) {
                                return <li key={i} className="ml-4">{line.substring(2)}</li>;
                              }
                              if (line.trim() === '') {
                                return <br key={i} />;
                              }
                              // Handle inline bold
                              const parts = line.split(/(\*\*[^*]+\*\*)/g);
                              return (
                                <p key={i} className="my-1">
                                  {parts.map((part, j) => {
                                    if (part.startsWith('**') && part.endsWith('**')) {
                                      return <strong key={j}>{part.replace(/\*\*/g, '')}</strong>;
                                    }
                                    return part;
                                  })}
                                </p>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
